import { useState, useEffect, useCallback } from "react"
import SallerCard from "../../elements/saller_card"
import SallerList from "../../elements/saller_list"
import { BASE_URL } from '../../apiConfig'
import Loading from "../../cors/Loading"
import { toast } from 'sonner'
import { useAuth } from "../../context/AuthProvider";
import { useNavigate } from 'react-router-dom';

export default function SallerPage() {
  const { token } = useAuth();
  const router = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState("grid")
  const [sortBy, setSortBy] = useState("name")
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [sellers, setSellers] = useState([])
  const [total, setTotal] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    activeToday: 0,
    newThisMonth: 0
  })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 30,
    total: 0
  })

  const fetchSellers = useCallback(async (page = 1 , search = "") => {
  try {
    const url = new URL(`${BASE_URL}/api/getsallers`);
    url.searchParams.append('page', page);
    if (search) {
      url.searchParams.append('search', search);
    }
    console.log('Fetching from:', url.toString()); 
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include' 
    });

    if (!response.ok) {
      let errorText;
      try {
        errorText = await response.text();
        console.error('Error response text:', errorText); // Debug log
        const errorData = errorText.startsWith('{') ? JSON.parse(errorText) : null;
        throw new Error(errorData?.message || errorText || 'Échec de la récupération des vendeurs');
      } catch (e) {
        console.error('Error parsing error response:', e); // Debug log
        throw new Error(errorText || 'Échec de la récupération des vendeurs');
      }
    }

    const data = await response.json();

    if (data.code === 200) {
      setSellers(data.data);
      setTotal(data.totals || {});
      setPagination({
        currentPage: data.pagination?.current_page || 1,
        lastPage: data.pagination?.last_page || 1,
        perPage: data.pagination?.per_page || 30,
        total: data.pagination?.total || 0
      });
      
      const now = new Date();
      const activeToday = data.data.filter((seller) => {
        const lastLogin = seller.info?.last_login_at;
        return lastLogin && new Date(lastLogin).toDateString() === now.toDateString();
      }).length;

      const newThisMonth = data.data.filter((seller) => {
        const created = seller.info?.created_at ? new Date(seller.info.created_at) : now;
        return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
      }).length;

      setStats({
        activeToday,
        newThisMonth
      });
    } else {
      throw new Error(data.message || 'Échec de la récupération des vendeurs');
    }
  } catch (error) {
    console.error('Fetch error:', error); // Debug log
    setError(error.message);
    toast.error(error.message || "Erreur lors du chargement des vendeurs.");
    
    // Redirect if unauthorized
    if (error.message.includes('Unauthorized') || error.message.includes('token')) {
      router('/login');
    }
  } finally {
    setLoading(false);
  }
}, [token, router]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSellers(1, searchTerm)
    }, 500) 

    return () => clearTimeout(timer)
  }, [searchTerm, fetchSellers])

  const handleRefresh = () => {
    fetchSellers(pagination.currentPage, searchTerm)
    toast.success('Liste des vendeurs actualisée')
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.lastPage) {
      fetchSellers(newPage, searchTerm)
    }
  }

  const filteredSellers = sellers
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.info.name.localeCompare(b.info.name)
        case "email":
          return a.email.localeCompare(b.email)
        case "last_login":
          // Utilisation de updated_at comme fallback pour last login
          return new Date(b.info.last_login_at).getTime() - new Date(a.info.last_login_at).getTime()
        case "newest":
          return new Date(b.info.created_at).getTime() - new Date(a.info.created_at).getTime()
        default:
          return 0
      }
    })

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12">
        {/* En-tête */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
          <div>
            <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
              Gestion des Vendeurs
            </h1>
            <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl">Gérez les vendeurs et leur activité</p>
          </div>
          
          <div className="mt-6 md:mt-0 flex gap-3">
            <button 
              onClick={handleRefresh}
              className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-sm">Actualiser</span>
            </button>
          </div>
        </div>

        {/* Section Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Vendeurs</p>
                <p className="text-2xl font-bold text-[var(--color-primary-500)]">{total.all || 0}</p>
              </div>
              <div className="w-12 h-12 bg-[var(--color-primary-50)] rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Actifs aujourd'hui</p>
                <p className="text-2xl font-bold text-green-600">{total.active_today || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Nouveaux ce mois</p>
                <p className="text-2xl font-bold text-purple-600">{total.new_this_month || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mb-4">
          <div className="bg-green-500 rounded-full pl-6 pr-1 py-1 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm font-medium text-white">Vendeurs actifs</p>
                <p className="text-lg font-bold text-white">{total.active || 0}</p>
              </div>
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-[#f06548] rounded-full pl-6 pr-1 py-1 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm font-medium text-white">Vendeurs inactifs</p>
                <p className="text-lg font-bold text-white">{total.inactive || 0}</p>
              </div>
              <div className="w-8 h-8 bg-red-50 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Recherche et Contrôles */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 px-4 py-2 max-xl:p-4 mb-5">
          <div className="flex flex-col items-center xl:flex-row gap-6 max-xl:gap-2">
            {/* Barre de recherche */}
            <div className="relative flex-1 py-2 w-full">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Rechercher des vendeurs par nom, email ou boutique..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full outline-none text-sm pl-12 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)] transition-all duration-200"
              />
            </div>

            {/* Contrôles */}
            <div className="flex gap-4 max-xl:w-full">
              {/* Menu déroulant de tri */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex text-xs cursor-pointer items-center gap-2 px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-[var(--color-primary-500)] transition-all duration-200"
                >
                  <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                    />
                  </svg>
                  <span>Trier</span>
                </button>

                {showSortDropdown && (
                  <div className="absolute cursor-pointer top-full mt-2 left-0 w-48 bg-white border-2 border-gray-200 rounded-2xl shadow-xl z-10">
                    <div className="py-1">
                      {[
                        { value: "name", label: "Nom (A-Z)" },
                        { value: "email", label: "Email (A-Z)" },
                        { value: "newest", label: "Plus récents d'abord" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value)
                            setShowSortDropdown(false)
                          }}
                          className={`w-full cursor-pointer text-xs text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                            sortBy === option.value ? "bg-[var(--color-primary-50)] text-[var(--color-primary-500)]" : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Toggle de vue */}
              <div className="flex bg-gray-100 max-h-max rounded-xl ml-2 overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-3 cursor-pointer max-h-max transition-colors ${
                    viewMode === "grid" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-3 cursor-pointer max-h-max transition-colors ${
                    viewMode === "list" ? "bg-[var(--color-primary-500)] text-white" : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback de chargement et erreur */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loading className="text-5xl text-[var(--color-primary-500)]" />
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={() => fetchSellers(pagination.currentPage, searchTerm)}
              className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Réessayer
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Nombre de résultats */}
            <div className="mb-4 flex justify-between items-center">
              <span className="inline-flex items-center px-3 py-1 bg-[var(--color-primary-50)] text-[var(--color-primary-500)] rounded-full text-xs font-medium">
                {pagination.total || 0} vendeurs trouvés
              </span>
              
              {sellers.length > 0 && (
                <div className="text-xs text-gray-500">
                  Trié par: {sortBy === 'name' ? 'Nom' : sortBy === 'email' ? 'Email' : 'Plus récents d\'abord'}
                </div>
              )}
            </div>

            {/* Affichage des vendeurs */}
            {sellers.length > 0 ? (
              viewMode === "grid" ? (
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 [.media(min-width:1400px)]:grid-cols-4 2xl:grid-cols-5 gap-6">  
                {filteredSellers.map((seller) => (
                    <SallerCard key={seller.id} seller={seller} fetchSellers={fetchSellers} Page={pagination?.currentPage} searchTerm={searchTerm} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredSellers.map((seller) => (
                    <SallerList key={seller.id} seller={seller} fetchSellers={fetchSellers} Page={pagination?.currentPage} searchTerm={searchTerm} />
                  ))}
                </div>
              )
            ) : (
              /* État vide */
              <div className="text-center py-5">
                <div className="mx-auto mb-6 w-20 h-20 bg-[var(--color-primary-50)] rounded-full flex items-center justify-center">
                  <svg className="h-10 w-10 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Aucun vendeur trouvé</h3>
                <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                  {searchTerm ? 'Aucun vendeur ne correspond à vos critères de recherche.' : 'Il n\'y a actuellement aucun vendeur dans le système.'}
                </p>
              </div>
            )}

            {/* Pagination */}
            {sellers.length > 0 && pagination.lastPage > 1 && (
              <div className="flex justify-center mt-8">
                <nav className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                    className="px-3 py-1 cursor-pointer text-xs rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Précédent
                  </button>
                  
                  {Array.from({ length: pagination.lastPage }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-2.5 py-1 cursor-pointer text-xs rounded-lg ${
                        page === pagination.currentPage
                          ? 'bg-[var(--color-primary-500)] text-white'
                          : 'border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.lastPage}
                    className="px-3 py-1 text-xs cursor-pointer rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}